/**
 * CodeRefactoringDashboard - Development-only tool for code analysis and refactoring
 * 
 * This component displays:
 * - Database schema (tables, columns, types, computed fields)
 * - Codebase structure (folders, files, components, hooks)
 * - Documentation files
 * - Architecture overview and explanations
 * 
 * ACCESS RESTRICTED: Only accessible to specific developer emails or superadmin.
 * NEVER deploy this to production.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

// Allowed developers (must match backend)
const ALLOWED_EMAILS = [
  'yrezaei@laframboise.net',
  'jeamon@laframboise.net',
];

// ============================================================================
// ARCHITECTURE EXPLANATIONS - Add documentation for each part of the system
// ============================================================================

const TABLE_EXPLANATIONS = {
  // Core tables
  users: 'User accounts. Linked to Azure AD via azure_object_id. Contains roles and permissions.',
  roles: 'Permission roles (superadmin, admin, manager, viewer). Defines access levels.',
  permissions: 'Granular permissions per role (e.g., matrix.upload, users.manage).',
  role_permissions: 'Junction table linking roles to permissions.',
  user_roles: 'Junction table linking users to roles.',
  
  // Projects
  projects: 'Main project records. Contains job_number, job_name, client info, status.',
  project_assignments: 'Links users to projects with specific roles (PM, lead, viewer).',
  project_disciplines: 'Disciplines enabled per project (piping, hvac, structural, etc.).',
  
  // Matrix/Progress Tracking
  matrix_data: 'Core progress tracking data. Stores line items with their progress status.',
  matrix_columns: 'Column definitions for matrix views. Configurable per project.',
  matrix_history: 'Audit trail of all matrix data changes with user/timestamp.',
  column_mappings: 'Maps uploaded Excel columns to matrix columns.',
  
  // Actual Spent Hours
  actual_spent_hours: 'Timesheet entries. Hours worked per discipline/activity.',
  actual_spent_templates: 'Predefined templates for time entry categories.',
  
  // Material Management
  materials: 'Material items with specifications, quantities, costs.',
  material_requisitions: 'Purchase requisitions for materials.',
  tariffs: 'Material pricing/tariff information.',
  purchasing_list: 'Active purchasing/procurement items.',
  
  // Estimation
  mto_data: 'Material Take-Off data for estimating.',
  labour_rates: 'Labour cost rates by trade/classification.',
  rentals: 'Equipment rental costs and schedules.',
  material_index: 'Material pricing indices for estimation.',
  
  // Documentation
  transmittals: 'Document transmittals with revision tracking.',
  documents: 'Document metadata and storage references.',
  rfis: 'Requests for Information.',
  submittals: 'Submittal documents for approval.',
  
  // Development
  development_log: 'Development task tracking and bug reports.',
  change_orders: 'Project change orders with cost impacts.',
  
  // Employees
  employees: 'Employee roster synced from external systems. Maps to portal roles.',
  employee_portal_roles: 'Links employees to their default portal roles.',
};

const FOLDER_EXPLANATIONS = {
  // Frontend folders
  'lgltools-react/src/components': 'React components organized by feature/module.',
  'lgltools-react/src/components/Matrix': 'Progress tracking matrix views (DisciplineMatrix, ActiveViewers).',
  'lgltools-react/src/components/Project': 'Project-level components (ProjectPage, ProjectList).',
  'lgltools-react/src/components/Actuals': 'Actual spent hours tracking and dashboards.',
  'lgltools-react/src/components/MaterialManagement': 'Material, tariff, purchasing components.',
  'lgltools-react/src/components/Estimation': 'MTO, labour rates, rentals, material index.',
  'lgltools-react/src/components/Documentation': 'Transmittals, RFIs, submittals.',
  'lgltools-react/src/components/Development': 'Development log, code refactoring tools.',
  'lgltools-react/src/components/ProgressTracking': 'Discipline-specific trackers (HVAC, Ducts, etc.).',
  'lgltools-react/src/components/common': 'Shared/reusable components.',
  'lgltools-react/src/components/Layout': 'App layout, navigation, sidebar, header.',
  'lgltools-react/src/components/Admin': 'Admin panels for users, roles, permissions.',
  'lgltools-react/src/hooks': 'Custom React hooks for data fetching, state, utilities.',
  'lgltools-react/src/utils': 'Utility functions (API client, formatters, validators).',
  'lgltools-react/src/config': 'Configuration files (menu structure, discipline configs).',
  
  // Backend folders
  'src/routes': 'Express route handlers for API endpoints.',
  'src/middleware': 'Express middleware (auth, permissions, logging).',
  'src/models': 'Database models and data access layer.',
  'src/socket': 'Socket.IO handlers for real-time features.',
  'src/services': 'Business logic and external service integrations.',
  'src/config': 'Server configuration files.',
};

const HOOK_EXPLANATIONS = {
  usePresence: 'Real-time presence tracking via Socket.IO. Shows who is viewing what.',
  useMatrixPresence: 'Matrix-specific wrapper for usePresence. Combines projectId + discipline.',
  useColumnMapping: 'Manages Excel column to matrix column mappings.',
  useMatrixData: 'Fetches and manages matrix data with caching.',
  useColumnResize: 'Handles resizable column widths in tables.',
  useFilterSort: 'Combined filtering and sorting for table data.',
  useExport: 'Handles data export to Excel/CSV.',
  useToast: 'Toast notification system.',
  useAnalyticsSettings: 'Persists user analytics preferences to localStorage.',
  useLocalStorage: 'Generic localStorage hook with JSON serialization.',
  useDebounce: 'Debounces rapidly changing values.',
  usePermissions: 'Checks user permissions for UI rendering.',
};

const COMPONENT_EXPLANATIONS = {
  // Major components
  DisciplineMatrix: 'Main progress tracking matrix. Renders any discipline (piping, hvac, etc.).',
  ProjectPage: 'Project wrapper. Handles routing to different project modules.',
  ActiveViewers: 'Shows who is currently viewing a resource (presence indicator).',
  TrackingToolbar: 'Standardized toolbar for tracking dashboards.',
  ExportModal: 'Modal for configuring and triggering exports.',
  
  // Logs
  ActualSpentLog: 'Timesheet/hours tracking log.',
  MaterialLog: 'Material management log.',
  TariffLog: 'Tariff/pricing log.',
  TransmittalLog: 'Document transmittal log.',
  DevelopmentLog: 'Development tasks/bugs log.',
  
  // Analytics
  ActualSpentDashboard: 'Analytics for actual spent hours.',
  ProgressAnalyticsDashboard: 'Analytics for progress tracking matrices.',
  
  // Common
  Toast: 'Notification toast component.',
  LoadingSpinner: 'Loading indicator.',
  ConfirmDialog: 'Confirmation modal.',
};

const ARCHITECTURE_OVERVIEW = `
## LGL Portal Architecture

### Frontend (lgltools-react)
- **React 18** with functional components and hooks
- **MSAL** for Azure AD authentication
- **Axios** for API calls with automatic token management
- **Socket.IO Client** for real-time features
- **No state management library** - uses React Context + local state

### Backend (src)
- **Express.js** server
- **PostgreSQL** database with connection pooling
- **Socket.IO** for WebSocket connections
- **Redis** for caching and session storage
- **JWT** tokens validated against Azure AD

### Key Patterns
1. **Authenticated API Calls**: All API calls go through \`authenticatedApiCall()\` which handles token refresh
2. **Permission-based UI**: Components check permissions before rendering actions
3. **Real-time Updates**: Socket.IO for presence, notifications, live data
4. **Excel-like Grids**: Custom table components with resize, sort, filter
5. **Discipline Config**: Each discipline (piping, hvac) has its own config file

### Data Flow
1. User logs in via Azure AD → MSAL token stored
2. API calls include Bearer token → Backend validates with Azure
3. User info + roles loaded from database
4. Permission checks gate access to features
5. Data changes broadcast via Socket.IO to other viewers

### File Naming Conventions
- \`*Log.js\` - Main data entry/viewing component
- \`*Dashboard.js\` - Analytics/visualization component
- \`*Modal.js\` - Modal dialog component
- \`use*.js\` - Custom React hook
- \`*Tracking.js\` - Progress tracking component
`;

const CodeRefactoringDashboard = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTables, setExpandedTables] = useState(new Set());
  const [expandedFolders, setExpandedFolders] = useState(new Set(['lgltools-react/src', 'src']));
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState(null);

  // Check if current user is allowed (email list OR superadmin role - backend handles this)
  const userEmail = accounts[0]?.username?.toLowerCase() || '';
  const [hasAccess, setHasAccess] = useState(true);

  // Fetch codebase analysis
  const fetchAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', '/code-refactoring/analysis');
      setData(response.data?.data || response.data);
      setHasAccess(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setHasAccess(false);
        setError('Access denied. This tool is restricted to authorized developers.');
      } else {
        setError(err.message || 'Failed to fetch codebase analysis');
      }
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  // Analyze a specific file
  const analyzeFile = async (filePath) => {
    setSelectedFile(filePath);
    setFileAnalysis(null);
    try {
      const response = await authenticatedApiCall(
        instance, 
        accounts, 
        'GET',
        `/code-refactoring/file-analysis?filePath=${encodeURIComponent(filePath)}`
      );
      setFileAnalysis(response.data?.data || response.data);
    } catch (err) {
      setFileAnalysis({ error: err.message });
    }
  };

  // Toggle table expansion
  const toggleTable = (tableName) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  };

  // Toggle folder expansion
  const toggleFolder = (folderPath) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderPath)) next.delete(folderPath);
      else next.add(folderPath);
      return next;
    });
  };

  // Filter database tables by search
  const filteredTables = useMemo(() => {
    if (!data?.database || !Array.isArray(data.database)) return [];
    if (!searchQuery) return data.database;
    const q = searchQuery.toLowerCase();
    return data.database.filter(t => 
      t.name.toLowerCase().includes(q) ||
      t.columns.some(c => c.name.toLowerCase().includes(q)) ||
      (TABLE_EXPLANATIONS[t.name] || '').toLowerCase().includes(q)
    );
  }, [data?.database, searchQuery]);

  // Colors
  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    dark: '#1e293b',
    light: '#f1f5f9',
    border: '#e2e8f0',
  };

  // Access denied view
  if (!hasAccess) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', padding: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ margin: '0 0 0.5rem', color: colors.dark }}>Access Denied</h2>
        <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
          This tool is restricted to authorized developers only.
          Your email ({userEmail || 'not logged in'}) is not in the allowed list.
        </p>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ fontSize: '2rem', marginBottom: '1rem', animation: 'spin 1s linear infinite' }}>⚙️</div>
        <p style={{ color: '#64748b' }}>Analyzing codebase...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', padding: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
        <h2 style={{ margin: '0 0 0.5rem', color: colors.danger }}>Error</h2>
        <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>{error}</p>
        <button onClick={fetchAnalysis} style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>
          Retry
        </button>
      </div>
    );
  }

  // Render file tree recursively
  const FileTree = ({ node, depth = 0, parentPath = '' }) => {
    if (!node) return null;
    
    const isExpanded = expandedFolders.has(node.path);
    const isDirectory = node.type === 'directory';
    const explanation = FOLDER_EXPLANATIONS[node.path];
    
    return (
      <div style={{ marginLeft: depth * 16 }}>
        <div
          onClick={() => {
            if (isDirectory) toggleFolder(node.path);
            else if (node.extension === '.js') analyzeFile(node.path);
          }}
          style={{
            padding: '4px 8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '4px',
            backgroundColor: selectedFile === node.path ? colors.light : 'transparent',
            color: isDirectory ? colors.dark : '#64748b',
            fontSize: '0.85rem',
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.light}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedFile === node.path ? colors.light : 'transparent'}
          title={explanation || ''}
        >
          <span style={{ width: '16px', textAlign: 'center' }}>
            {isDirectory ? (isExpanded ? '📂' : '📁') : '📄'}
          </span>
          <span style={{ fontWeight: isDirectory ? '500' : '400' }}>{node.name}</span>
          {explanation && <span style={{ fontSize: '0.7rem', color: colors.success }}>ℹ️</span>}
        </div>
        {isDirectory && isExpanded && node.children && (
          <div>
            {node.children.map((child, idx) => (
              <FileTree key={idx} node={child} depth={depth + 1} parentPath={node.path} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // ============================================================================
  // TAB RENDERERS
  // ============================================================================

  const renderOverviewTab = () => (
    <div style={{ padding: '1rem', overflowY: 'auto', height: '100%' }}>
      {/* Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.primary }}>{Array.isArray(data?.database) ? data.database.length : 0}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Database Tables</div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.success }}>{Array.isArray(data?.database) ? data.database.reduce((sum, t) => sum + (t.columnCount || 0), 0) : 0}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Columns</div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.secondary }}>{data?.documentation?.length || 0}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Doc Files</div>
        </div>
        <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.warning }}>{Object.keys(HOOK_EXPLANATIONS).length}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Custom Hooks</div>
        </div>
      </div>

      {/* Architecture Overview */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: colors.dark, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          🏗️ Architecture Overview
        </h3>
        <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui', fontSize: '0.9rem', lineHeight: '1.6', color: '#374151' }}>
          {ARCHITECTURE_OVERVIEW.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return <h3 key={i} style={{ color: colors.primary, marginTop: '1rem' }}>{line.replace('## ', '')}</h3>;
            if (line.startsWith('### ')) return <h4 key={i} style={{ color: colors.dark, marginTop: '0.75rem' }}>{line.replace('### ', '')}</h4>;
            if (line.startsWith('- **')) {
              const match = line.match(/- \*\*(.+?)\*\*(.*)$/);
              if (match) return <div key={i} style={{ marginLeft: '1rem', marginBottom: '0.25rem' }}>• <strong>{match[1]}</strong>{match[2]}</div>;
            }
            if (line.match(/^\d\./)) return <div key={i} style={{ marginLeft: '1rem', marginBottom: '0.25rem' }}>{line}</div>;
            if (line.startsWith('- ')) return <div key={i} style={{ marginLeft: '1rem', marginBottom: '0.25rem' }}>• {line.replace('- ', '')}</div>;
            return <div key={i}>{line}</div>;
          })}
        </div>
      </div>

      {/* Key Hooks */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: colors.dark, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          🪝 Custom Hooks Reference
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.75rem' }}>
          {Object.entries(HOOK_EXPLANATIONS).map(([name, desc]) => (
            <div key={name} style={{ padding: '0.75rem', backgroundColor: colors.light, borderRadius: '6px', borderLeft: `3px solid ${colors.secondary}` }}>
              <code style={{ fontWeight: '600', color: colors.secondary }}>{name}</code>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Components */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: colors.dark, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          🧩 Key Components Reference
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.75rem' }}>
          {Object.entries(COMPONENT_EXPLANATIONS).map(([name, desc]) => (
            <div key={name} style={{ padding: '0.75rem', backgroundColor: colors.light, borderRadius: '6px', borderLeft: `3px solid ${colors.primary}` }}>
              <code style={{ fontWeight: '600', color: colors.primary }}>{name}</code>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderDatabaseTab = () => (
    <div style={{ display: 'flex', gap: '1rem', height: '100%' }}>
      {/* Table list */}
      <div style={{ width: '350px', borderRight: `1px solid ${colors.border}`, paddingRight: '1rem', overflowY: 'auto' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '0.9rem' }}>
          Tables ({filteredTables.length})
        </h4>
        {filteredTables.map((table, idx) => (
          <div key={idx} style={{ marginBottom: '0.5rem' }}>
            <div
              onClick={() => toggleTable(table.name)}
              style={{
                padding: '8px 12px',
                backgroundColor: expandedTables.has(table.name) ? colors.light : 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                border: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: '500', color: colors.dark, fontSize: '0.85rem' }}>
                  {expandedTables.has(table.name) ? '▼' : '▶'} {table.name}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{table.columnCount} cols</span>
              </div>
              {TABLE_EXPLANATIONS[table.name] && (
                <div style={{ fontSize: '0.75rem', color: colors.success, marginTop: '4px', fontStyle: 'italic' }}>
                  {TABLE_EXPLANATIONS[table.name]}
                </div>
              )}
            </div>
            {expandedTables.has(table.name) && (
              <div style={{ marginTop: '4px', padding: '8px', backgroundColor: '#f8fafc', borderRadius: '4px', fontSize: '0.8rem' }}>
                {table.columns.map((col, cidx) => (
                  <div key={cidx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: cidx < table.columns.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                    <span style={{ color: colors.dark, fontFamily: 'monospace' }}>{col.name}</span>
                    <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                      {col.type}
                      {col.nullable && <span style={{ color: '#94a3b8', marginLeft: '4px' }}>NULL</span>}
                      {col.isComputed && <span style={{ color: colors.warning, marginLeft: '4px' }} title="Auto-generated">⚡</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Summary & Legend */}
      <div style={{ flex: 1, padding: '0 1rem', overflowY: 'auto' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark }}>Database Summary</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.primary }}>{Array.isArray(data?.database) ? data.database.length : 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Tables</div>
          </div>
          <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: colors.success }}>
              {Array.isArray(data?.database) ? data.database.reduce((sum, t) => sum + (t.columnCount || 0), 0) : 0}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>Total Columns</div>
          </div>
        </div>
        
        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, padding: '1rem', marginBottom: '1rem' }}>
          <h5 style={{ margin: '0 0 0.75rem', color: colors.dark }}>Legend</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.8rem', color: '#64748b' }}>
            <span>⚡ Auto-generated (nextval, timestamp)</span>
            <span>NULL - Nullable column</span>
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}`, padding: '1rem' }}>
          <h5 style={{ margin: '0 0 0.75rem', color: colors.dark }}>Table Categories</h5>
          <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
            <div><strong>Core:</strong> users, roles, permissions</div>
            <div><strong>Projects:</strong> projects, project_assignments, project_disciplines</div>
            <div><strong>Matrix:</strong> matrix_data, matrix_columns, matrix_history</div>
            <div><strong>Actuals:</strong> actual_spent_hours, actual_spent_templates</div>
            <div><strong>Materials:</strong> materials, material_requisitions, tariffs, purchasing_list</div>
            <div><strong>Estimation:</strong> mto_data, labour_rates, rentals, material_index</div>
            <div><strong>Docs:</strong> transmittals, documents, rfis, submittals</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCodebaseTab = () => (
    <div style={{ display: 'flex', gap: '1rem', height: '100%' }}>
      {/* File tree */}
      <div style={{ width: '400px', borderRight: `1px solid ${colors.border}`, paddingRight: '1rem', overflowY: 'auto' }}>
        <h4 style={{ margin: '0 0 0.5rem', color: colors.dark, fontSize: '0.9rem' }}>📦 Frontend (lgltools-react)</h4>
        <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.5rem' }}>React app, components, hooks, utils</p>
        <FileTree node={data?.frontend} />
        
        <h4 style={{ margin: '1.5rem 0 0.5rem', color: colors.dark, fontSize: '0.9rem' }}>⚙️ Backend (src)</h4>
        <p style={{ fontSize: '0.75rem', color: '#64748b', margin: '0 0 0.5rem' }}>Express routes, middleware, models</p>
        <FileTree node={data?.backend} />
      </div>
      
      {/* File analysis */}
      <div style={{ flex: 1, padding: '0 1rem', overflowY: 'auto' }}>
        {selectedFile ? (
          <>
            <h4 style={{ margin: '0 0 1rem', color: colors.dark }}>
              File: <code style={{ backgroundColor: colors.light, padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem' }}>{selectedFile}</code>
            </h4>
            {fileAnalysis ? (
              fileAnalysis.error ? (
                <p style={{ color: colors.danger }}>{fileAnalysis.error}</p>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                    <h5 style={{ margin: '0 0 0.5rem', color: colors.dark }}>📊 Basic Info</h5>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Lines of code: {fileAnalysis.lineCount}</p>
                  </div>
                  
                  {fileAnalysis.components?.length > 0 && (
                    <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                      <h5 style={{ margin: '0 0 0.5rem', color: colors.primary }}>🧩 Components ({fileAnalysis.components.length})</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {fileAnalysis.components.map((c, i) => (
                          <span key={i} style={{ padding: '4px 8px', backgroundColor: colors.light, borderRadius: '4px', fontSize: '0.8rem' }}>
                            {c}
                            {COMPONENT_EXPLANATIONS[c] && <span title={COMPONENT_EXPLANATIONS[c]} style={{ marginLeft: '4px', color: colors.success }}>ℹ️</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {fileAnalysis.hooks?.length > 0 && (
                    <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                      <h5 style={{ margin: '0 0 0.5rem', color: colors.secondary }}>🪝 Hooks ({fileAnalysis.hooks.length})</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {fileAnalysis.hooks.map((h, i) => (
                          <span key={i} style={{ padding: '4px 8px', backgroundColor: colors.light, borderRadius: '4px', fontSize: '0.8rem' }}>
                            {h}
                            {HOOK_EXPLANATIONS[h] && <span title={HOOK_EXPLANATIONS[h]} style={{ marginLeft: '4px', color: colors.success }}>ℹ️</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {fileAnalysis.imports?.length > 0 && (
                    <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                      <h5 style={{ margin: '0 0 0.5rem', color: colors.warning }}>📥 Imports ({fileAnalysis.imports.length})</h5>
                      <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', maxHeight: '150px', overflowY: 'auto' }}>
                        {fileAnalysis.imports.map((imp, i) => (
                          <div key={i} style={{ padding: '2px 0', color: '#64748b' }}>{imp}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {fileAnalysis.exports?.length > 0 && (
                    <div style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
                      <h5 style={{ margin: '0 0 0.5rem', color: colors.success }}>📤 Exports ({fileAnalysis.exports.length})</h5>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {fileAnalysis.exports.map((e, i) => (
                          <span key={i} style={{ padding: '4px 8px', backgroundColor: colors.light, borderRadius: '4px', fontSize: '0.8rem' }}>
                            {e.isDefault && '⭐ '}{e.name} <span style={{ color: '#94a3b8' }}>({e.type})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            ) : (
              <p style={{ color: '#64748b' }}>Loading analysis...</p>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
            <p style={{ margin: 0 }}>Select a .js file to analyze</p>
            <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>Click on any file in the tree to see its components, hooks, imports, and exports</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderDocsTab = () => (
    <div style={{ padding: '1rem', overflowY: 'auto', height: '100%' }}>
      <h4 style={{ margin: '0 0 1rem', color: colors.dark }}>📝 Documentation Files</h4>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Markdown documentation in the <code>/documentation</code> folder
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
        {data?.documentation?.map((doc, idx) => (
          <div key={idx} style={{ padding: '1rem', backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📄</span>
              <span style={{ fontWeight: '500', color: colors.dark }}>{doc.name}</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px', fontFamily: 'monospace' }}>{doc.path}</div>
          </div>
        ))}
        {(!data?.documentation || data.documentation.length === 0) && (
          <p style={{ color: '#64748b' }}>No documentation files found in /documentation folder.</p>
        )}
      </div>
      
      {/* Add helpful tips */}
      <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: colors.light, borderRadius: '8px' }}>
        <h5 style={{ margin: '0 0 0.5rem', color: colors.dark }}>💡 Documentation Tips</h5>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#64748b', fontSize: '0.85rem', lineHeight: '1.6' }}>
          <li>Add new .md files to <code>/documentation</code> folder</li>
          <li>Use clear naming: <code>FEATURE_NAME.md</code></li>
          <li>Include: purpose, usage, API reference, examples</li>
          <li>Keep docs updated when code changes</li>
        </ul>
      </div>
    </div>
  );

  // Main tabs
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '🏠' },
    { id: 'database', label: 'Database', icon: '🗃️' },
    { id: 'codebase', label: 'Codebase', icon: '📂' },
    { id: 'docs', label: 'Documentation', icon: '📝' },
  ];

  return (
    <div style={{ padding: '1rem', height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: colors.dark }}>🔧 Code Refactoring Dashboard</h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
            Development tool for codebase analysis • Last updated: {data?.timestamp ? new Date(data.timestamp).toLocaleString() : 'N/A'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search tables, columns, folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '0.9rem', width: '280px' }}
          />
          <button
            onClick={fetchAnalysis}
            style={{ padding: '8px 16px', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: `1px solid ${colors.border}`, paddingBottom: '0.5rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: selectedTab === tab.id ? colors.primary : 'transparent',
              color: selectedTab === tab.id ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'hidden', backgroundColor: '#fafbfc', borderRadius: '8px', padding: '1rem' }}>
        {selectedTab === 'overview' && renderOverviewTab()}
        {selectedTab === 'database' && renderDatabaseTab()}
        {selectedTab === 'codebase' && renderCodebaseTab()}
        {selectedTab === 'docs' && renderDocsTab()}
      </div>

      {/* Warning banner */}
      <div style={{
        marginTop: '1rem',
        padding: '0.5rem 1rem',
        backgroundColor: '#fef3c7',
        border: '1px solid #f59e0b',
        borderRadius: '6px',
        fontSize: '0.8rem',
        color: '#92400e',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <span>⚠️</span>
        <span>Development-only tool. Never deploy to production. Access: {ALLOWED_EMAILS.join(', ')} + superadmins</span>
      </div>
    </div>
  );
};

export default CodeRefactoringDashboard;
